<template>
  <div class="page-container" v-if="currentNews && currentNews.title">
    <h1 class="page-title">{{ currentNews.title }}</h1>
    <p class="page-date" v-if="currentNews.date">{{ currentNews.date }}</p>
    <div class="content" :class="langStore.currentLang.startsWith('zh') ? 'lang-zh' : 'lang-en'">
      <template v-for="(section, index) in contentSections" :key="index">
        <div class="content-section">
          <div class="text-block">
            <p
              :class="{
                'no-indent': isReferenceSection(section),
                'highlighted-paragraph': isHighlightedParagraph(section)
              }"
              v-html="section"
            ></p>
          </div>
        </div>
        
        <!-- After section 3: picture1 (未来在...综合能源网络等领域前景广阔) -->
        <div v-if="index === 3 && currentNews.other?.picture1?.url" class="content-section image-section">
          <div class="image-container">
            <img :src="currentNews.other.picture1.url">
          </div>
        </div>

        <!-- After section 9: picture2, picture3 (低热泄漏...实测热泄漏低至0.8 W/m) -->
        <div v-if="index === 10" class="content-section image-section">
          <div v-if="currentNews.other?.picture2?.url" class="image-container">
            <img :src="currentNews.other.picture2.url">
          </div>
          <div v-if="currentNews.other?.picture3?.url" class="image-container">
            <img :src="currentNews.other.picture3.url">
          </div>
        </div>

        <!-- After section 11: picture4, picture5, picture6 (高电流应用...电解工业用直流超导电缆达1 kV/20 kA) -->
        <div v-if="index === 18" class="content-section image-section">
          <div v-if="currentNews.other?.picture4?.url" class="image-container">
            <img :src="currentNews.other.picture4.url">
          </div>
          <div v-if="currentNews.other?.picture5?.url" class="image-container">
            <img :src="currentNews.other.picture5.url">
          </div>
          <div v-if="currentNews.other?.picture6?.url" class="image-container">
            <img :src="currentNews.other.picture6.url">
          </div>
        </div>

        <!-- After section 15: picture7, picture8 (铁路馈电...增强蒙帕纳斯火车站的供电能力) -->
        <div v-if="index === 27" class="content-section image-section">
          <div v-if="currentNews.other?.picture7?.url" class="image-container">
            <img :src="currentNews.other.picture7.url">
          </div>
          <div v-if="currentNews.other?.picture8?.url" class="image-container">
            <img :src="currentNews.other.picture8.url">
          </div>
        </div>

        <!-- After section 17: picture9, picture10 (灵活性...适用于空间受限的场合) -->
        <div v-if="index === 31" class="content-section image-section">
          <div v-if="currentNews.other?.picture9?.url" class="image-container">
            <img :src="currentNews.other.picture9.url">
          </div>
          <div v-if="currentNews.other?.picture10?.url" class="image-container">
            <img :src="currentNews.other.picture10.url">
          </div>
        </div>

        <!-- After section 36: picture11, picture12, picture13 -->
        <div v-if="index === 39" class="content-section image-section">
          <div v-if="currentNews.other?.picture11?.url" class="image-container">
            <img :src="currentNews.other.picture11.url">
          </div>
          <div v-if="currentNews.other?.picture12?.url" class="image-container">
            <img :src="currentNews.other.picture12.url">
          </div>
          <div v-if="currentNews.other?.picture13?.url" class="image-container">
            <img :src="currentNews.other.picture13.url">
          </div>
        </div>

        <!-- After section 40: picture14 -->
        <div v-if="index === 43 && currentNews.other?.picture14?.url" class="content-section image-section">
          <div class="image-container">
            <img :src="currentNews.other.picture14.url">
          </div>
        </div>

      </template>
    </div>
  </div>
  <div v-else class="page-container">
    <p>新闻内容未找到</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useLangStore } from '../../../pinia/stores/lang';
import zhData from '../../../i18n/zh';
import enData from '../../../i18n/en';

const langStore = useLangStore();

const currentNews = computed(() => {
  const translationData = langStore.currentLang.startsWith('zh') ? zhData : enData;
  const newsList = translationData.pages.news.company.news.list;
  const newsItem = newsList.find((news: any) => news.id === 'chaodaodianlanluxiantu');
  return newsItem || {};
});

const contentSections = computed(() => {
  const content = (currentNews.value as any)?.content || '';
  return content ? content.split('---').filter((p: string) => p.trim()) : [];
});

const isReferenceSection = (section: string) => {
  const text = section.trim();
  return (
    text.startsWith('链接：') ||
    text.startsWith('Source:') ||
    text.startsWith('Links：') ||
    text.startsWith('DOI')
  );
};

const isHighlightedParagraph = (section: string) => {
  const text = section.trim();
  return (
    text.startsWith('超导电缆技术正处于') ||
    text.startsWith('This paper concludes that superconducting cable') ||
    /^\d+\./.test(text)
  );
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/content-blocks.scss";

.page-container {
  overflow: auto;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Microsoft YaHei', Arial, 'PingFang SC', sans-serif;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  text-align: center;
}

.page-date {
  color: #666;
  font-size: 14px;
  margin-bottom: 30px;
  text-align: right;
}

.content-section {
  margin-bottom: 20px;
}

.text-block {
  /* 中文正文首行缩进 2 个汉字位 */
  .content.lang-zh & p {
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    margin-bottom: 15px;
    text-align: justify;
    text-indent: 2em;
  }

  /* 英文正文不缩进 */
  .content.lang-en & p {
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    margin-bottom: 15px;
    text-align: justify;
    text-indent: 0;
  }

  p.no-indent {
    text-indent: 0;
  }

  &.highlighted-paragraph {
    color: #0066cc;
    font-weight: bold;
  }

  /* 小标题蓝色 */
  :deep(h3) {
    color: #0066cc;
    font-weight: bold;
    margin-top: 12px;
    margin-bottom: 12px;
    font-size: 18px;
  }

  /* 链接蓝色可点击 */
  :deep(a) {
    color: #1677ff;
    text-decoration: underline;
    cursor: pointer;
  }

  :deep(a:hover) {
    color: #0958d9;
  }
}

.image-section {
  text-align: center;
}

.image-container {
  display: inline-block;
  margin: 10px 0;
}

.image-container img {
  max-width: 100%;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.image-caption {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}
</style>
